import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, FileText, Lock, Package, X } from "lucide-react";
import api, { resolveMedia } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { AVAIL_LABELS } from "@/lib/shopUtils";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—";

export default function Progetto() {
  useSEO({ title: "Il tuo progetto — Giovanna Matino Interior", description: "La tua Shopping List personalizzata con le condizioni riservate ai clienti dello Studio." });
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/studio/${key}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Link non valido o non più attivo."));
  }, [key]);

  if (err) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="project-invalid">
        <div className="max-w-lg text-center bg-white border border-hair p-10">
          <X size={26} className="mx-auto text-taupe" />
          <h1 className="font-serif text-3xl mt-4">Questo accesso non è attivo.</h1>
          <p className="text-taupe mt-4 text-sm">{err} Se pensi sia un errore, contatta lo Studio.</p>
          <Link to="/contatti" className="btn-outline-ink mt-8 inline-flex">Contattaci</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="container-x py-24 text-taupe">Caricamento…</div>;

  return (
    <div data-testid="page-project">
      <section className="bg-ink text-bone">
        <div className="container-x pt-12 pb-14 md:pt-16">
          <div className="flex items-center gap-2 text-gold text-[10px] tracking-[0.16em] uppercase"><Lock size={12} /> Area riservata · Shop the Project</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-5">Il tuo progetto{data.contact_name ? `, ${data.contact_name}` : ""}.</h1>
          <p className="mt-4 text-bone/70 font-light max-w-2xl">
            {data.project_name} — la selezione dello Studio con le tue condizioni riservate
            {data.discount_percent ? ` (−${data.discount_percent}% sui prodotti idonei)` : ""}, già applicate ai prezzi che vedi qui e nello Shop.
          </p>
          {data.expires_at && <p className="mt-2 text-[11px] text-bone/50">Accesso valido fino al {fmtDate(data.expires_at)}.</p>}
          {data.pdf_url && (
            <a href={resolveMedia(data.pdf_url)} target="_blank" rel="noopener" className="mt-6 inline-flex items-center gap-2 border border-bone/50 px-6 py-3 min-h-[44px] text-xs tracking-[0.16em] uppercase hover:bg-bone hover:text-ink transition-colors" data-testid="project-pdf">
              <FileText size={15} /> Scarica la Shopping List (PDF)
            </a>
          )}
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="eyebrow text-gold mb-6">La tua selezione</div>
        <div className="space-y-4" data-testid="project-items">
          {data.items.map((it, i) => (
            <div key={`${it.product_id}-${i}`} className="bg-white border border-hair p-4 md:p-6 flex gap-4 md:gap-6 items-start flex-wrap" data-testid={`project-item-${it.product_id}`}>
              <Link to={`/shop/${it.product_id}?studio=${key}`} className="shrink-0">
                {it.image ? <img src={resolveMedia(it.image)} alt={it.name} className="w-24 h-24 md:w-28 md:h-28 object-cover" loading="lazy" /> : <div className="w-24 h-24 bg-bone flex items-center justify-center"><Package size={20} className="text-taupe" /></div>}
              </Link>
              <div className="flex-1 min-w-[220px]">
                {it.brand && <div className="text-[10px] tracking-[0.14em] uppercase text-gold">{it.brand}</div>}
                <Link to={`/shop/${it.product_id}?studio=${key}`} className="font-serif text-xl md:text-2xl hover:text-gold transition-colors">{it.name}</Link>
                <div className="text-xs text-taupe mt-1">{[it.variant, it.finish, it.room].filter(Boolean).join(" · ")}</div>
                <div className="text-[11px] text-taupe mt-1">
                  {AVAIL_LABELS[it.availability] || "Disponibile"}{it.lead_time ? ` · Tempi indicativi: ${it.lead_time}` : ""} · Quantità: {it.quantity}
                </div>
                {it.note && <p className="text-xs text-taupe italic mt-2 border-l-2 border-gold/40 pl-3">Nota dello Studio: {it.note}</p>}
              </div>
              <div className="text-right shrink-0">
                {it.studio_eligible && it.price_reserved < it.price_list ? (
                  <>
                    <div className="text-xs text-taupe line-through">{eur(it.line_total_list)}</div>
                    <div className="font-serif text-2xl text-gold">{eur(it.line_total_reserved)}</div>
                    <div className="text-[10px] tracking-[0.12em] uppercase text-gold">Vantaggio cliente</div>
                  </>
                ) : (
                  <div className="font-serif text-2xl">{eur(it.line_total_list)}</div>
                )}
                <Link to={`/shop/${it.product_id}?studio=${key}`}
                  className="mt-3 inline-flex items-center gap-2 bg-ink text-bone px-5 py-2.5 min-h-[44px] text-xs tracking-[0.14em] uppercase hover:bg-gold transition-colors"
                  data-testid={`project-buy-${it.product_id}`}>
                  Acquista <ArrowUpRight size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-ink text-bone p-8 md:p-10 grid sm:grid-cols-3 gap-6 text-center" data-testid="project-totals">
          <div><div className="eyebrow !text-bone/50 !text-[10px]">Totale di listino</div><div className="font-serif text-3xl mt-2">{eur(data.totals.listino)}</div></div>
          <div><div className="eyebrow !text-gold !text-[10px]">Il tuo vantaggio</div><div className="font-serif text-3xl mt-2 text-gold">−{eur(data.totals.vantaggio)}</div></div>
          <div><div className="eyebrow !text-bone/50 !text-[10px]">Totale Shop the Project</div><div className="font-serif text-3xl mt-2">{eur(data.totals.riservato)}</div></div>
        </div>
        <p className="text-[11px] text-taupe mt-4">Prezzi, disponibilità e tempi di consegna verificati alla data di generazione della Shopping List e soggetti a variazione.</p>

        {data.orders.length > 0 && (
          <div className="mt-14" data-testid="project-orders">
            <div className="eyebrow text-gold mb-5">I tuoi ordini</div>
            <div className="space-y-3">
              {data.orders.map((o) => (
                <div key={o.order_number} className="bg-white border border-hair p-4 flex justify-between items-center flex-wrap gap-2">
                  <span className="text-sm">{o.order_number} · {fmtDate(o.created_at)}</span>
                  <span className="text-sm text-taupe">{eur(o.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
